/**
 * ConfirmOnExitDialog Component
 *
 * Prompts the user to fill a required field before a card can leave a column.
 * Used when a column has a confirmOnExit rule configured.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  ConfirmOnExitConfig,
  CONFIRMABLE_FIELD_OPTIONS,
  ConfirmableField,
} from '@/types/board-config.types';
import type { BoardTask } from '@/types/models/BoardTask';
import { useThemeColors } from '@/styles';

interface ConfirmOnExitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  card: BoardTask;
  fieldConfig: ConfirmOnExitConfig;
  onConfirm: (value: number) => void;
  isSubmitting: boolean;
}

function getFieldOption(field: ConfirmableField) {
  return CONFIRMABLE_FIELD_OPTIONS.find(o => o.value === field);
}

export function ConfirmOnExitDialog({
  open,
  onOpenChange,
  card,
  fieldConfig,
  onConfirm,
  isSubmitting,
}: ConfirmOnExitDialogProps) {
  const colors = useThemeColors();
  const option = getFieldOption(fieldConfig.field);
  const existingValue =
    (card[fieldConfig.field as keyof BoardTask] as number | undefined) ?? null;
  const [value, setValue] = useState<string>(
    existingValue != null && existingValue > 0 ? String(existingValue) : ''
  );

  // Reset value when dialog opens with a new card
  useEffect(() => {
    if (open) {
      const existing =
        (card[fieldConfig.field as keyof BoardTask] as number | undefined) ??
        null;
      setValue(existing != null && existing > 0 ? String(existing) : '');
    }
  }, [open, card, fieldConfig.field]);

  const numericValue = parseInt(value, 10);
  const isValid = !isNaN(numericValue) && numericValue > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      onConfirm(numericValue);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{option?.label ?? 'Required Field'}</DialogTitle>
            <DialogDescription>
              {option?.prompt ??
                'Please fill in the required field before moving this card.'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <input
              type="number"
              min={1}
              step={1}
              value={value}
              onChange={e => setValue(e.target.value)}
              autoFocus
              className="w-full px-3 py-2 rounded-md border text-sm"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border.default,
                color: colors.text.primary,
              }}
              placeholder="Enter a number"
            />
          </div>

          <DialogFooter className="sm:justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Confirm & Move'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
